import { useSyncExternalStore } from "react";

const STORAGE_KEY = "devSiteOverrides";
const CHANGE_EVENT = "devsiteschange";

/**
 * Sites whose URL contains this keyword are treated as development sites
 * automatically, without needing an explicit override.
 */
export const DEV_SITE_URL_KEYWORD = "empyreanstudio";

/**
 * Overrides are stored as { [siteKey]: boolean }. An entry is only present when
 * an admin explicitly marked (true) or unmarked (false) a site, so the keyword
 * rule stays in charge of everything else.
 */
let cachedOverrides = null;

const parseOverrides = (raw) => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    return Object.keys(parsed).reduce((acc, key) => {
      acc[key] = parsed[key] === true;
      return acc;
    }, {});
  } catch (error) {
    console.error("Failed to read dev site overrides:", error);
    return {};
  }
};

export const readDevSiteOverrides = () => {
  if (!cachedOverrides) {
    cachedOverrides = parseOverrides(localStorage.getItem(STORAGE_KEY));
  }
  return cachedOverrides;
};

const writeDevSiteOverrides = (overrides) => {
  cachedOverrides = overrides;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
  } catch (error) {
    console.error("Failed to save dev site overrides:", error);
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
};

/**
 * Re-applies overrides that were wiped by `localStorage.clear()` on logout.
 * Dev site marks are configuration, not session data.
 */
export const restoreDevSiteOverrides = (overrides) => {
  writeDevSiteOverrides(overrides || {});
};

/**
 * Stable identity for a site. instanceId survives re-installs, so prefer it and
 * fall back to the row id only when the instance is unknown.
 */
export const getDevSiteKey = (installation) => {
  const instanceId = installation?.instanceId;
  if (instanceId && instanceId !== "—") return String(instanceId);
  const id = installation?.id;
  if (id === null || id === undefined || id === "—") return "";
  return `id:${id}`;
};

/** True when the site URL matches the auto-detection keyword. */
export const hasDevSiteUrl = (installation) => {
  const url = installation?.url;
  return (
    typeof url === "string" && url.toLowerCase().includes(DEV_SITE_URL_KEYWORD)
  );
};

export const isDevSite = (installation, overrides = readDevSiteOverrides()) => {
  const key = getDevSiteKey(installation);
  if (key && Object.prototype.hasOwnProperty.call(overrides, key)) {
    return overrides[key];
  }
  return hasDevSiteUrl(installation);
};

export const setDevSite = (installation, isDev) => {
  const key = getDevSiteKey(installation);
  if (!key) return;
  writeDevSiteOverrides({
    ...readDevSiteOverrides(),
    [key]: isDev === true,
  });
};

/**
 * Marks every site whose URL contains the keyword as a development site.
 * Returns a summary so the caller can report what the job actually changed.
 */
export const markMatchingSitesAsDev = (installations = []) => {
  const next = { ...readDevSiteOverrides() };
  let marked = 0;
  let alreadyMarked = 0;

  installations.forEach((installation) => {
    if (!hasDevSiteUrl(installation)) return;
    const key = getDevSiteKey(installation);
    if (!key) return;
    if (next[key] === true) {
      alreadyMarked += 1;
      return;
    }
    next[key] = true;
    marked += 1;
  });

  if (marked > 0) writeDevSiteOverrides(next);

  return { matched: marked + alreadyMarked, marked, alreadyMarked };
};

/** Number of sites explicitly marked as development sites. */
export const countMarkedDevSites = (overrides = readDevSiteOverrides()) =>
  Object.keys(overrides).filter((key) => overrides[key] === true).length;

export const clearDevSiteOverrides = () => writeDevSiteOverrides({});

export const subscribeToDevSites = (listener) => {
  const handleStorage = (event) => {
    if (event.key === null || event.key === STORAGE_KEY) {
      cachedOverrides = null;
      listener();
    }
  };
  window.addEventListener(CHANGE_EVENT, listener);
  window.addEventListener("storage", handleStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, listener);
    window.removeEventListener("storage", handleStorage);
  };
};

/** Subscribes a component to dev site changes made anywhere in the app. */
export const useDevSiteOverrides = () =>
  useSyncExternalStore(subscribeToDevSites, readDevSiteOverrides);
